import { useState, useEffect, useCallback } from "react";
import "./styles/global.css";

import { Preloader }      from "./components/shared/Preloader";
import { Landing }        from "./components/shared/Landing";
import { LoginPage }      from "./components/shared/LoginPage";
import { SignupPage }     from "./components/shared/SignupPage";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { UserPortal }     from "./components/user/UserPortal";

import { api, storeToken, clearToken, hasToken } from "./api";

export default function App() {
  const [screen, setScreen]           = useState("landing");
  const [currentUser, setCurrentUser] = useState(null);
  const [products, setProducts]       = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [orders, setOrders]           = useState([]);
  const [dark, setDark]               = useState(() => localStorage.getItem("kd_dark") === "1");
  const [showPreloader, setShowPreloader] = useState(() => !sessionStorage.getItem("kd_preloaded"));
  const [screenKey, setScreenKey]     = useState(0); // forces re-mount for page-enter animation

  // ─────────────────────────────────────────────────────────────
  //  HISTORY NAVIGATION (Back Button Support)
  // ─────────────────────────────────────────────────────────────
  const navigateToScreen = (nextScreen) => {
    if (nextScreen === screen) return;

    let path = "/";
    if (nextScreen === "admin-login") path = "/admin";
    // We can add other paths here later if needed (e.g., /login, /signup)

    window.history.pushState({ screen: nextScreen }, "", path);
    setScreen(nextScreen);
    setScreenKey(k => k + 1);
  };

  useEffect(() => {
    const handlePopState = (e) => {
      // Prioritize explicit state if it exists
      if (e.state && e.state.screen) {
        setScreen(e.state.screen);
        setScreenKey(k => k + 1);
      } else {
        // Fallback to path-based detection if state is missing (e.g. manual path typing)
        const path = window.location.pathname;
        if (path === "/admin") {
          setScreen("admin-login");
        } else {
          setScreen("landing");
        }
        setScreenKey(k => k + 1);
      }
    };
    window.addEventListener("popstate", handlePopState);

    // Initial path detection on mount
    const initialPath = window.location.pathname;
    if (initialPath === "/admin") {
      setScreen("admin-login");
      window.history.replaceState({ screen: "admin-login" }, "", "/admin");
    } else if (!window.history.state) {
      window.history.replaceState({ screen: "landing" }, "", "/");
    }

    return () => window.removeEventListener("popstate", handlePopState);
  }, [screen]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    localStorage.setItem("kd_dark", dark ? "1" : "0");
  }, [dark]);

  // Restore session on mount
  useEffect(() => {
    if (!hasToken()) return;
    api("/api/auth/me")
      .then(({ user }) => { setCurrentUser(user); navigateToScreen(user.role === "admin" ? "admin" : "user"); })
      .catch(() => clearToken());
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try { 
      const data = await api(`/api/products?t=${Date.now()}`, { auth: false }); 
      setProducts(data); 
    } catch (err) {
      console.error("[App] fetchProducts Error:", err);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try { const data = await api(`/api/orders?t=${Date.now()}`); setOrders(data); } catch {}
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (screen === "admin" || screen === "user") { 
      fetchOrders(); 
      fetchProducts();
    }
  }, [screen, fetchOrders, fetchProducts]);

  const login = (user, token, remember = true) => { storeToken(token, remember); setCurrentUser(user); navigateToScreen(user.role === "admin" ? "admin" : "user"); };
  const logout = () => { clearToken(); setCurrentUser(null); setProducts([]); setOrders([]); navigateToScreen("landing"); };

  const handleUpdateProducts = async (updatedList) => {
    setProducts(updatedList);
  };

  const handleUpdateOrders = async (updatedList) => {
    setOrders(updatedList);
  };

  const handlePlaceOrder = async (orderData) => {
    const order = await api("/api/orders", { method: "POST", body: orderData });
    setOrders(prev => [order, ...prev]);
    return order;
  };

  const handlePreloaderComplete = () => {
    setShowPreloader(false);
    sessionStorage.setItem("kd_preloaded", "1");
  };

  const p = { dark, setDark };
  return (
    <>
      {showPreloader && <Preloader onComplete={handlePreloaderComplete} />}
      {screen === "landing"     && <Landing key={screenKey} onNav={navigateToScreen} {...p} />}
      {screen === "admin-login" && <LoginPage key={screenKey} type="admin" onBack={() => navigateToScreen("landing")} onLogin={login} {...p} />}
      {screen === "user-login"  && <LoginPage key={screenKey} type="user"  onBack={() => navigateToScreen("landing")} onLogin={login} onGoSignup={() => navigateToScreen("user-signup")} {...p} />}
      {screen === "user-signup" && <SignupPage key={screenKey} onBack={() => navigateToScreen("user-login")} onSignupComplete={login} {...p} />}
      {screen === "admin" && currentUser && (
        <AdminDashboard user={currentUser} products={products} orders={orders}
          onLogout={logout} onUpdateProducts={handleUpdateProducts} onUpdateOrders={handleUpdateOrders} {...p} />
      )}
      {screen === "user" && currentUser && (
        <UserPortal user={currentUser} products={products} loading={loadingProducts} userOrders={orders}
          onLogout={logout} onPlaceOrder={handlePlaceOrder} {...p} />
      )}
    </>
  );
}
